'use client'

import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useTranslations } from 'next-intl'
import { Container, HStack, Text, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState, useCallback } from 'react'

import { IRedux } from '@/app/[locale]/_store/types'
import { ClockIcon } from '@/app/[locale]/_assets/icon-components'
import getShipperById from '@/app/[locale]/_service/shipper.service'
import { isWorkingHour } from '@/app/[locale]/_utils/isWorkingHour'
import ConfirmModal from '@/app/[locale]/_components/ConfirmModal'
import Time from '@/app/[locale]/_components/Time'
import { useSaveBranchData } from '@/app/[locale]/_hooks/useSaveBranchData'
import { branchService } from '@/app/[locale]/_service/branch.service'
import { findActivePoint } from '@/app/[locale]/_utils/location'
import { sessionActions } from '@/app/[locale]/_store/session/session.slice'

interface WorkingHours {
  start: string
  end: string
}

const AlertWorkingHours = () => {
  const [isOpen, setIsOpen] = useState(false)

  const { session, common, future_time, settings } = useSelector(
    (state: IRedux) => ({
      session: state.session,
      common: state.common,
      future_time: state.checkout?.future_time,
      settings: state.settings,
    }),
    shallowEqual
  )

  const { order_type, permissions, offHours, is_hall } = session
  const { branch, deliveryZone, points } = common

  const t = useTranslations()
  const dispatch = useDispatch()
  const saveBranch = useSaveBranchData()
  const { open, onOpen, onClose } = useDisclosure()

  const showPreorder =
    settings?.data?.data?.show_preorder ||
    typeof settings?.data?.data?.show_preorder == 'undefined'

  const activePoint = useMemo(() => {
    if (!is_hall) return findActivePoint(points)
    return null
  }, [is_hall, points])

  // future nearest branch
  const findFutureNearestBranch = useCallback(
    async ({
      lat,
      long,
      date_time,
    }: {
      lat: number
      long: number
      date_time: string
    }) => {
      try {
        const result = await branchService.findNearestByTime({
          lat,
          long,
          order_type_id: order_type?.id,
          date_time,
        })

        return result?.data?.branches?.find((b: any) => b.is_active) || null
      } catch (error) {
        console.error('Error while finding future nearest branch', error)
        return null
      }
    },
    [order_type?.id]
  )

  // nearest branch
  const findNearestBranch = useCallback(
    async ({ lat, long }: { lat: number; long: number }) => {
      try {
        const result = await branchService.findNearest({
          lat,
          long,
          order_type_id: order_type?.id,
          delivery_zone_id: deliveryZone?.id || '',
        })

        return result?.data?.branches?.find((b: any) => b.is_active) || null
      } catch (error) {
        console.error('Error while finding nearest branch', error)
        return null
      }
    },
    [order_type?.id, deliveryZone?.id]
  )

  const fetchNearestBranch = useCallback(async () => {
    if (activePoint?.location?.[0] && activePoint?.location?.[1]) {
      let nearestBranch: any = null

      if (future_time) {
        nearestBranch = await branchService.findNearestByTime({
          lat: activePoint?.location?.[0],
          long: activePoint?.location?.[1],
          order_type_id: order_type?.id,
          date_time: future_time,
        })
      } else {
        nearestBranch = await findNearestBranch({
          lat: activePoint.location[0],
          long: activePoint.location[1],
        })
      }

      if (nearestBranch?.id) {
        saveBranch(nearestBranch)
      } else {
        getShipperById(settings?.data?.shipper_id || '')
          .then((res) => {
            dispatch(sessionActions.save({ shipper: res?.data }))
          })
          .catch((error) => {
            console.error('Error while fetching shipper', error)
          })
      }
    }
  }, [
    future_time,
    branch,
    findFutureNearestBranch,
    findNearestBranch,
    dispatch,
  ])

  // auto fetch branch
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     if (order_type?.type === 'delivery') {
  //       fetchNearestBranch()
  //     }
  //   }, 500)

  //   return () => clearTimeout(timeoutId)
  // }, [order_type?.type, fetchNearestBranch])

  const workingHours = useMemo<WorkingHours | null>(() => {
    if (
      branch?.work_hour_start &&
      branch?.work_hour_end &&
      !isWorkingHour(branch.work_hour_start, branch.work_hour_end)
    ) {
      return { start: branch.work_hour_start, end: branch.work_hour_end }
    }
    if (
      session?.shipper?.work_hour_start &&
      session?.shipper?.work_hour_end &&
      !isWorkingHour(
        session?.shipper?.work_hour_start,
        session?.shipper?.work_hour_end
      )
    ) {
      return {
        start: session?.shipper?.work_hour_start,
        end: session?.shipper?.work_hour_end,
      }
    }
    return null
  }, [branch, session?.shipper])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (
        branch?.work_hour_start &&
        branch?.work_hour_end &&
        !future_time &&
        !isWorkingHour(branch.work_hour_start, branch.work_hour_end)
      ) {
        setIsOpen(true)
        return
      }

      if (
        !branch?.id &&
        order_type?.type === 'delivery' &&
        !future_time &&
        permissions?.geolocation === 'accepted'
      ) {
        setIsOpen(true)
      }
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [branch, future_time, permissions?.geolocation, order_type])

  const handleOffHours = (shouldOpenTime: boolean) => {
    if (shouldOpenTime) {
      onOpen()
    } else {
      setIsOpen(false)
    }
    dispatch(sessionActions.setOffHours())
  }

  const message = workingHours
    ? order_type?.type === 'delivery'
      ? t(
          showPreorder
            ? 'only_in_home_page.delivery_starts_working'
            : 'only_in_home_page.delivery_starts_working_no_preorder',
          {
            start: workingHours.start,
            end: workingHours.end,
          }
        )
      : t(
          showPreorder
            ? 'only_in_home_page.establishment_starts_working'
            : 'only_in_home_page.establishment_starts_working_no_preorder',
          {
            start: workingHours.start,
            end: workingHours.end,
          }
        )
    : ''

  if (branch?.is_work_24_7) {
    return null
  }

  return (
    <>
      {/* Banner Alert */}
      {workingHours && (
        <Container mt={{ base: 6, lg: 0 }}>
          <HStack
            bg='dark'
            p={4}
            mt={{ base: 0, lg: 5 }}
            rounded={16}
            gap='10px'
          >
            <ClockIcon />
            <Text fontSize='sm' color='light' w='full'>
              {message}
            </Text>
          </HStack>
        </Container>
      )}
      <ConfirmModal
        title={t('attention')}
        isOpen={isOpen && !offHours}
        onConfirm={() => handleOffHours(true)}
        onClose={() => handleOffHours(false)}
        description={
          t('we_are_currently_closed') +
          ' ' +
          (showPreorder ? t('do_you_preorder') : '')
        }
        text={{
          confirm: showPreorder ? 'pre_order' : undefined,
          cancel: 'menu',
        }}
      />
      <Time onOpen={onOpen} onClose={onClose} open={open} showTrigger={false} />
    </>
  )
}

export default AlertWorkingHours
